/**
 * Harness Layer: Content store hook
 *
 * Central state management for the dashboard.
 * View components only interact through this hook — they don't
 * touch raw data directly and don't know about localStorage.
 *
 * Exposes:
 *   posts       - current post list (with favorite state)
 *   filters     - current filter params
 *   filtered    - posts after applying filters (derived)
 *   groups      - filtered posts grouped by date (derived)
 *   stats       - aggregate counts (derived)
 *   bookList    - unique books with counts (derived from all posts)
 *   dateList    - unique dates with counts (derived from all posts)
 *   tagList     - top tags with counts (derived from all posts)
 *
 * Actions:
 *   setFilter(key, value)
 *   clearFilters()
 *   toggleFavorite(postId)
 *   clearFavorites()
 *   openDetail(postId)
 *   closeDetail()
 */

// T-Cron-2026-10-24 14:00 (round-2-of-day) — 改进清单 #9「统计:view count」。
//
// 浏览次数是**使用者本地行为**(localStorage),不是内容字段 —— 刻意不放进
// posts 里(那会让 450 条数据每次都多带一个数字,徒增内存),而是单独存一份
// {postId: count} 映射,按需查。
//
// 本文件是 .jsx 且含 JSX ⇒ React 由 vite.config.js 的 esbuild.jsxInject
// 全局注入,这里**不要**显式 import React。
import { loadViewCounts, recordView, clearViewCounts } from './viewCounts.js';

const STORAGE_KEY = 'xhs_dashboard_favorites_v1';

/**
 * Load favorite IDs from localStorage.
 * @returns {string[]}
 */
function loadFavorites() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

/**
 * Save favorite IDs to localStorage.
 * @param {string[]} ids
 */
function saveFavorites(ids) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch (e) {
    // ignore
  }
}

function useContentStore() {
  // Initialize posts with persisted favorite state
  const [posts, setPosts] = React.useState(() => {
    const favIds = loadFavorites();
    return POSTS.map(p => ({ ...p, favorite: favIds.includes(p.id) }));
  });

  const [filters, setFilters] = React.useState({
    search: '',
    book: '',
    date: '',
    hotspot: '',
    tag: '',
    angle: '',
    source: '',
    version: '',
    category: '',
    batch: '',
    onlyFavorites: false,
  });

  const [detailId, setDetailId] = React.useState(null);

  // T-Cron-2026-10-24 14:00 (round-2-of-day) — 改进清单 #9「view count」。
  //
  // 状态形状 = { postId: count },来自 harness/viewCounts.js(localStorage)。
  //
  // 为什么放在 store 而不是各组件自己读:
  //   Header 要显示「本次看过 N 篇」的总览、PostDetail 要显示「已看 X 次」——
  //   两处必须看到**同一份**数字。各自读 localStorage 会出现"详情里显示 3 次、
  //   header 显示 0 次"的分裂(读取时机不同 ⇒ React 渲染时机也不同)。
  //
  // 为什么不用 useState + useEffect 去同步 localStorage:
  //   那是"多一份真相"。这里的真相只有 localStorage 一份,state 只是它的**快照**,
  //   由 recordView / 初始化时读取来更新。
  const [viewCounts, setViewCounts] = React.useState(() => loadViewCounts());

  // Derived values via ContentService
  const filtered = React.useMemo(
    () => ContentService.applyFilters(posts, filters),
    [posts, filters]
  );

  const groups = React.useMemo(
    () => ContentService.groupByDate(filtered),
    [filtered]
  );

  const stats = React.useMemo(
    () => {
      // T-Cron-2026-10-23 08:00 — 改进清单 #9「统计:今日新增指示」。
      //
      // 「今日」的口径 = **数据集里 date 最大的那一天**,不是系统日期。
      // 理由与 ContentService.isToday 完全一致:看板是内容快照,跑批节奏是
      // 4 次/天(早/午/下午/晚),半夜或周末看的时候"系统今天"可能压根没有
      // 这一天的数据 ⇒ 按系统日期算会得到 0,指示器就成了噪音。
      const newestDate = posts.length
        ? posts.reduce((acc, p) => (p.date > acc ? p.date : acc), posts[0].date)
        : '';
      const todayCount = newestDate
        ? posts.reduce((n, p) => (p.date === newestDate ? n + 1 : n), 0)
        : 0;

      return {
        total: posts.length,
        visible: filtered.length,
        favorites: ContentService.countFavorites(posts),
        books: ContentService.getBookList(posts).filter(b => b.name).length,
        hotspots: ContentService.getHotspotList(posts).length,
        withCover: ContentService.countWithCover(posts),
        dateRange: ContentService.getDateRange(posts),
        lastUpdated: (typeof DATA_META !== 'undefined' && DATA_META.lastUpdated) || '',
        newestDate,
        todayCount,
      };
    },
    [posts, filtered]
  );

  const bookList = React.useMemo(
    () => ContentService.getBookList(posts),
    [posts]
  );

  const dateList = React.useMemo(
    () => ContentService.getDateList(posts),
    [posts]
  );

  const hotspotList = React.useMemo(
    () => ContentService.getHotspotList(posts),
    [posts]
  );

  const categoryList = React.useMemo(
    () => ContentService.getCategoryList(posts),
    [posts]
  );

  const batchList = React.useMemo(
    () => ContentService.getBatchList(posts),
    [posts]
  );

  const tagList = React.useMemo(
    () => ContentService.getTagList(posts, 25),
    [posts]
  );

  const angleList = React.useMemo(
    () => ContentService.getAngleList(posts),
    [posts]
  );

  const sourceList = React.useMemo(
    () => ContentService.getSourceList(posts),
    [posts]
  );

  const detailPost = React.useMemo(
    () => (detailId ? ContentService.findById(posts, detailId) : null),
    [posts, detailId]
  );

  // Actions
  const setFilter = React.useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = React.useCallback(() => {
    setFilters({
      search: '',
      book: '',
      date: '',
      hotspot: '',
      tag: '',
      angle: '',
      source: '',
      version: '',
      category: '',
      batch: '',
      onlyFavorites: false,
    });
  }, []);

  const toggleFavorite = React.useCallback((postId) => {
    setPosts(prev => {
      const next = ContentService.toggleFavorite(prev, postId);
      const favIds = next.filter(p => p.favorite).map(p => p.id);
      saveFavorites(favIds);
      return next;
    });
  }, []);

  const clearFavorites = React.useCallback(() => {
    setPosts(prev => prev.map(p => ({ ...p, favorite: false })));
    saveFavorites([]);
  }, []);

  // T-Cron-2026-10-24 02:00 早间 (round-1-of-day) — 改进清单 #8「批量操作」。
  //
  // 批量收藏需要的是「把这一批设成已收藏 / 取消收藏」,不是「逐个 toggle」。
  // 逐个 toggle 的坑:如果这批里有一篇本来就收藏了,逐个 toggle 会把它**取消**,
  // 结果和用户意图相反。⇒ 显式的 setFavorites(ids, value)。
  //
  // 复用既有的 saveFavorites 持久化通道,和 toggleFavorite / clearFavorites
  // 写的是同一个 localStorage key ⇒ 三条路径不会互相覆盖出脏数据。
  const setFavorites = React.useCallback((postIds, value) => {
    const idSet = new Set(postIds || []);
    if (idSet.size === 0) return;
    const next = !!value;
    setPosts(prev => {
      const updated = prev.map(p => (idSet.has(p.id) ? { ...p, favorite: next } : p));
      saveFavorites(updated.filter(p => p.favorite).map(p => p.id));
      return updated;
    });
  }, []);

  const openDetail = React.useCallback((postId) => {
    setDetailId(postId);
    document.body.style.overflow = 'hidden';
    // T-Cron-2026-10-24 14:00 (round-2-of-day) — 改进清单 #9「view count」。
    //
    // 记录点选在这里,而不是 PostDetail 的 useEffect:
    //   · openDetail 是**唯一的**"打开详情"入口(PostCard → App → store),
    //     放这儿就不会漏记,也不会重复记。
    //   · recordView 内部有「本次会话去重」(harness/viewCounts.js),
    //     所以 React 严格模式双调用 / 重复点击不会把计数刷上去。
    if (postId) {
      const next = recordView(postId);
      if (next > 0) {
        // 只更新这一个 id ⇒ 不会因为一次浏览重渲染整个 450 条列表。
        setViewCounts((prev) => (prev[postId] === next ? prev : { ...prev, [postId]: next }));
      }
    }
  }, []);

  const closeDetail = React.useCallback(() => {
    setDetailId(null);
    document.body.style.overflow = '';
  }, []);

  // T-Cron-2026-11-05 03:30 (T148) — 改进清单 #9「view count」的**隐私缺口**。
  //
  // 【缺口】harness/viewCounts.js 里的 clearViewCounts() 从上线起就是
  //   **死导出**:全仓零 UI 调用(T146/T147 已两次确认)。
  //   ⇒ 用户的本地浏览记录**永久累积、无法清除**。
  //   而浏览记录恰恰是**最敏感的一类本地数据**(「我读过什么」= 阅读画像)。
  //   「能积累但不能删除」在隐私上是最糟的形态 —— 别的应用至少给一条后路。
  //
  // 【为什么放在 store 而不是组件里直接调 harness】
  //   clearViewCounts() 清的是 localStorage,但 UI 上还有两处依赖同一份快照:
  //   · Header 的「已读 N 篇」胶囊(篇数)
  //   · PostDetail 的「已看 X 次」
  //   ⇒ 只清 localStorage 不清 React state ⇒ 两处会**显示旧数字**
  //     (且 Header 的篇数胶囊会变成「已读 0 篇」但缓存里还在)——
  //     用户以为没清掉,或者更糟:以为清掉了但下次打开又变回来。
  //   ⇒ 真相只有一份(localStorage),state 是它的快照 ⇒ 两处一起更新。
  const clearAllViewCounts = React.useCallback(() => {
    const ok = clearViewCounts();
    if (ok) setViewCounts({});
    return ok;
  }, []);

  // T-Cron-2026-10-24 14:00 (round-2-of-day) — 改进清单 #9「view count」。
  // 单独提成 useCallback(而不是写进 return 的对象字面量里):
  //   hook 写在对象字面量内部虽然合法,但会随对象每次重建而重新创建回调,
  //   依赖数组 [viewCounts] 的稳定性也难以判断。提到外面最稳。
  const getViewCount = React.useCallback((postId) => viewCounts[postId] || 0, [viewCounts]);

  return {
    // Data
    posts,
    filters,
    filtered,
    groups,
    stats,
    bookList,
    dateList,
    hotspotList,
    categoryList,
    batchList,
    tagList,
    angleList,
    sourceList,
    detailPost,
    detailId,
    // Actions
    setFilter,
    clearFilters,
    toggleFavorite,
    clearFavorites,
    setFavorites,
    openDetail,
    closeDetail,
    // T-Cron-2026-10-24 14:00 (round-2-of-day) — 改进清单 #9「view count」。
    // viewCounts: { postId: count } 快照;getViewCount(postId): 读单个。
    viewCounts,
    getViewCount,
    // T148:清除全部本地浏览记录(隐私后路)。返回是否真的清掉了。
    clearAllViewCounts,
  };
}

window.useContentStore = useContentStore;

export default useContentStore;
